import clsx from 'clsx';

import { css, styled } from '@mui/material';
import IcChevronDownSmallRegular from '@kep/kcds-icons/IcChevronDownSmallRegular';
import IcChevronUpSmallRegular from '@kep/kcds-icons/IcChevronUpSmallRegular';
import IcCloseSmallRegular from '@kep/kcds-icons/IcCloseSmallRegular';

import { useElementIsEllipsis } from '../../utils';
import { Box } from '../Box';
import { Tooltip } from '../Tooltip';
import { filterChipClasses } from './theme';

import type { SyntheticEvent } from 'react';
import type { FilterChipOwnerState, FilterChipProps } from './types';

const FilterChip = (props: FilterChipProps) => {
  const {
    label,
    onClickLabel,
    onDelete,
    open,
    disabled = false, // 기본값 설정
    slots,
    className,
    classes,
    ref,
  } = props;

  const ownerState: FilterChipOwnerState = {
    ...props,
    disabled,
  };

  // 동적 클래스만 생성 (고정 클래스는 target으로 처리)
  const getDynamicClasses = () => {
    return {
      root: clsx(
        // filterChipClasses.root는 target으로 자동 적용
        disabled && filterChipClasses.disabled,
        classes?.root,
        className,
      ),
      labelContainer: clsx(filterChipClasses.labelContainer, classes?.labelContainer),
      label: clsx(filterChipClasses.label, classes?.label, slots?.label),
      collapseIcon: clsx(filterChipClasses.collapseIcon, classes?.collapseIcon),
      deleteIcon: clsx(filterChipClasses.deleteIcon, classes?.deleteIcon, slots?.deleteIcon),
    };
  };

  const dynamicClasses = getDynamicClasses();
  const { isEllipsis, ref: labelEllipsisRef } = useElementIsEllipsis();

  const handleLabelClick = (event: SyntheticEvent<HTMLDivElement>) => {
    onClickLabel?.(event);
  };

  const handleLabelKeyDown = (event: SyntheticEvent<HTMLDivElement>) => {
    if (event.nativeEvent instanceof KeyboardEvent && event.nativeEvent.key === 'Enter') {
      onClickLabel?.(event);
    }
  };

  return (
    <FilterChipRoot ownerState={ownerState} className={dynamicClasses.root} ref={ref}>
      {label && (
        <FilterChipLabelContainer
          className={dynamicClasses.labelContainer}
          ownerState={ownerState}
          role="button"
          tabIndex={!disabled ? 0 : -1}
          onClick={handleLabelClick}
          onKeyDown={handleLabelKeyDown}
        >
          {isEllipsis ? (
            <Tooltip message={label}>
              <FilterChipLabel
                className={dynamicClasses.label}
                ownerState={ownerState}
                ref={labelEllipsisRef}
              >
                {label}
              </FilterChipLabel>
            </Tooltip>
          ) : (
            <FilterChipLabel
              className={dynamicClasses.label}
              ownerState={ownerState}
              ref={labelEllipsisRef}
            >
              {label}
            </FilterChipLabel>
          )}
          <FilterChipCollapseIcon className={dynamicClasses.collapseIcon} ownerState={ownerState}>
            {!open ? <IcChevronDownSmallRegular /> : <IcChevronUpSmallRegular />}
          </FilterChipCollapseIcon>
        </FilterChipLabelContainer>
      )}
      {onDelete && (
        <FilterChipDeleteIcon
          className={dynamicClasses.deleteIcon}
          ownerState={ownerState}
          onClick={onDelete}
          role="button"
          tabIndex={!disabled ? 0 : -1}
        >
          <IcCloseSmallRegular />
        </FilterChipDeleteIcon>
      )}
    </FilterChipRoot>
  );
};

export default FilterChip;

// Styled Components
const FilterChipRoot = styled(Box, {
  target: filterChipClasses.root,
})<{ ownerState: FilterChipOwnerState }>(({ ownerState, theme }) => {
  const { disabled } = ownerState;

  return css`
    display: inline-flex;
    align-items: center;
    background-color: ${theme.color['surface/informative/weakest/default']};
    border-radius: 1px;
    cursor: pointer;

    ${disabled &&
    css`
      pointer-events: none;
      background-color: ${theme.color['surface/neutral/weakest/disabled']};

      .${filterChipClasses.label} {
        color: ${theme.color['text/disabled']};
      }

      .${filterChipClasses.collapseIcon}, .${filterChipClasses.deleteIcon} {
        color: ${theme.color['icon/informative/disabled']};
      }
    `}
  `;
});

const FilterChipLabelContainer = styled(Box, {
  target: filterChipClasses.labelContainer,
})<{ ownerState: FilterChipOwnerState }>(({ theme }) => {
  return css`
    min-width: 0px;
    flex: 1 1 0;
    display: flex;
    padding: 4px 6px;
    border-radius: 1px;

    &:hover {
      background-color: ${theme.color['surface/informative/weakest/hover']};
    }

    &:active {
      background-color: ${theme.color['surface/informative/weakest/press']};
    }

    &:focus-visible {
      position: relative;
      ${theme.mixins.focusRing('inside', {
        borderRadius: 1,
        thickness: 1,
        selectors: { focus: '&' },
      })};
    }

    &:has(+ .${filterChipClasses.deleteIcon}) {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-right: 1px solid ${theme.color['border/weaker/default']};
    }
  `;
});

const FilterChipLabel = styled(Box, {
  target: filterChipClasses.label,
})<{ ownerState: FilterChipOwnerState }>(({ theme }) => {
  return css`
    flex: 1 1 0;
    ${theme.typography['label/medium/sm']}
    padding: 0 4px;
    color: ${theme.color['text/informative/strong/default']};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
});

const FilterChipCollapseIcon = styled(Box, {
  target: filterChipClasses.collapseIcon,
})<{ ownerState: FilterChipOwnerState }>(({ theme }) => {
  return css`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${theme.color['icon/informative/strong/default']};
  `;
});

const FilterChipDeleteIcon = styled(Box, {
  target: filterChipClasses.deleteIcon,
})<{ ownerState: FilterChipOwnerState }>(({ theme }) => {
  return css`
    border-radius: 1px;
    width: 28px;
    min-width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${theme.color['icon/informative/strong/default']};

    &:hover {
      background-color: ${theme.color['surface/informative/weakest/hover']};
    }

    &:active {
      background-color: ${theme.color['surface/informative/weakest/press']};
    }

    &:focus-visible {
      position: relative;
      ${theme.mixins.focusRing('inside', {
        borderRadius: 1,
        thickness: 1,
        selectors: {
          focus: '&',
        },
      })};
    }

    .${filterChipClasses.labelContainer} + & {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  `;
});
